import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { 
  Calendar, 
  Users, 
  BedDouble, 
  FileText, 
  Phone, 
  Mail, 
  Search,
  Building,
  FileDown
} from "lucide-react";
import ResourcePage from "../../components/ResourcePage";
import { api } from "../../axios/api";
import { notify } from "../../utils/toastAlert";

const ClientesCheckIn = () => {
  const { user, empresa } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [dadosCheckIn, setDadosCheckIn] = useState(null);

  const [filtros, setFiltros] = useState({
    inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    fim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
  });

  const fetchClientesCheckIn = async () => {
    try {
      setLoading(true);
      const response = await api.post("/caixa/clientes-checkin", {
        empresa_id: empresa?.id || user?.empresa_id,
        inicio: filtros.inicio,
        fim: filtros.fim
      });
      setDadosCheckIn(response.data?.data || response.data);
    } catch (err) {
      console.error("Erro ao carregar clientes com check-in:", err);
      notify("Erro ao carregar lista de check-ins.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empresa?.id || user?.empresa_id) {
      fetchClientesCheckIn();
    }
  }, [empresa?.id, user?.empresa_id]);

  const handleDateChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchClientesCheckIn();
  };

  const handleExportPDF = async () => {
    try {
      await notify("A gerar relatório de check-ins em PDF...", "info");
      // Adicione a chamada real para download de PDF se necessário
    } catch (err) {
      await notify("Erro ao exportar PDF.", "error");
    }
  };

  const totalCheckIns = dadosCheckIn?.total_checkins || 0;
  const listaClientes = dadosCheckIn?.clientes || [];

  return (
    <ResourcePage 
      title="Clientes com Check-in no Período" 
      breadcrumb={["Hotelaria", "Check-ins"]}
      actions={(
        <button 
          type="button" 
          className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1 shadow-sm"
          onClick={handleExportPDF}
        >
          <FileDown size={16} /> Exportar PDF
        </button>
      )}
    >
      {/* Filtro de Período */}
      <div className="card bg-white border-0 shadow-sm mb-4">
        <div className="card-body py-3">
          <form onSubmit={handleFilterSubmit} className="row align-items-center g-3">
            <div className="col-md-auto">
              <span className="fw-semibold text-muted d-flex align-items-center gap-1">
                <Calendar size={18} /> Período de Estadia:
              </span>
            </div>
            <div className="col-md-3">
              <input 
                type="date" 
                className="form-control form-control-sm" 
                name="inicio" 
                value={filtros.inicio} 
                onChange={handleDateChange} 
                required
              />
            </div>
            <div className="col-md-3">
              <input 
                type="date" 
                className="form-control form-control-sm" 
                name="fim" 
                value={filtros.fim} 
                onChange={handleDateChange} 
                required
              />
            </div>
            <div className="col-md-auto">
              <button 
                type="submit" 
                className="btn btn-primary btn-sm px-4" 
                disabled={loading}
              >
                {loading ? "A atualizar..." : "Filtrar Check-ins"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Cartão de Resumo */}
      <div className="row mb-4">
        <div className="col-xl-4 col-md-6">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-info bg-info-subtle p-2 rounded d-inline-flex mb-3">
                <Users size={22} />
              </div>
              <p className="text-muted mb-1 small">Total de Check-ins Registados</p>
              <h4 className="mb-0 fw-bold">{totalCheckIns} hóspedes / estadias</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Clientes com Check-in */}
      <div className="card bg-white border-0 shadow-sm">
        <div className="card-body">
          <h4 className="card-title mb-3 fs-5 d-flex align-items-center gap-2">
            <BedDouble size={20} className="text-primary" /> Hóspedes / Check-ins no Período
          </h4>
          <div className="table-responsive">
            <table className="table table-centered table-hover mb-0 align-middle">
              <thead>
                <tr className="text-muted fs-7">
                  <th>Cliente / Hóspede</th>
                  <th>Contacto & NIF</th>
                  <th>Quarto</th>
                  <th>Check-in / Check-out</th>
                  <th>Documento Associado</th>
                </tr>
              </thead>
              <tbody>
                {listaClientes.length > 0 ? (
                  listaClientes.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <span className="fw-semibold d-block">{item.cliente?.nome}</span>
                        <small className="text-muted d-flex align-items-center gap-1">
                          <Mail size={12} /> {item.cliente?.email}
                        </small>
                      </td>
                      <td>
                        <span className="d-block small text-dark">NIF: {item.cliente?.nif}</span>
                        <small className="text-muted d-flex align-items-center gap-1">
                          <Phone size={12} /> {item.cliente?.telefone}
                        </small>
                      </td>
                      <td>
                        <span className="badge bg-info-subtle text-info px-2 py-1">
                          {item.estadia?.quarto}
                        </span>
                      </td>
                      <td>
                        <span className="d-block small text-success fw-semibold">In: {item.estadia?.data_check_in}</span>
                        <span className="d-block small text-danger fw-semibold">Out: {item.estadia?.data_check_out}</span>
                      </td>
                      <td>
                        <span className="fw-semibold text-secondary">
                          {item.documento?.tipo} {item.documento?.numero}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">Nenhum check-in registado para este período.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ResourcePage>
  );
};

export default ClientesCheckIn;